<template>
  <el-row :class="{ compact: compact }">
    <el-col :span="span" class="promotion">
      <form-item
        :label="$t('Campaign_PromotionSetup_SelectPromotion')"
        v-if="!loading"
      >
        <el-select
          v-model="value.promotion_id"
          filterable
          clearable
          remote
          :remote-method="searchPromotions"
          @visible-change="handleVisibleChange"
          @change="selectPromotion"
          :loading="loading"
          :placeholder="
            $t('Campaign_PromotionSetup_SelectPromotion_Placeholder')
          "
        >
          <el-option-group
            v-for="(group, groupIndex) in promotionOptionGroups"
            :key="groupIndex"
            :label="group.name"
          >
            <el-option
              v-for="(item, index) in group.items"
              :key="index"
              :label="item.name"
              :value="item.id"
              class="dropdown-item"
              :disabled="group.disabled"
            >
              <span class="label">{{ item.name }}</span>
              <div class="description" v-if="item.description">
                {{ item.description }}
              </div>
              <div class="tags">
                <el-tag type="info" size="small" v-if="item.integration_name">
                  {{ item.integration_name }}
                </el-tag>
                <el-tag type="info" size="small" v-if="item.external_id">
                  ID: {{ item.external_id }}
                </el-tag>
              </div>
            </el-option>
          </el-option-group>
        </el-select>
      </form-item>
    </el-col>

    <el-col :span="span" class="params">
      <form-item
        :label="$t('Campaign_PromotionSetup_SelectAction')"
        v-if="selectedPromotionActions.length > 1"
      >
        <el-select
          v-model="value.promotion_action"
          :placeholder="$t('Campaign_PromotionSetup_SelectAction_Placeholder')"
        >
          <el-option
            v-for="(item, index) in selectedPromotionActions"
            :key="index"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </form-item>

      <form-item
        v-for="param in selectedPromotionActionParams"
        :label="param.title"
        :key="param.name"
        class="amount"
      >
        <el-row v-if="param.name === 'amount'">
          <el-col :span="compact ? 24 : 8">
            <el-select
              v-model="value.promotion_amount_type"
              @change="handleAmountTypeChange"
              class="promotion-amount-type"
            >
              <el-option value="fixed" label="Fixed Amount" />
              <el-option value="attribute" label="User Attribute" />
              <el-option
                value="metric"
                label="User Metric"
                v-if="$hasFeature('accumulation')"
              />
              <el-option
                value="event"
                label="Trigger Event"
                v-if="value.trigger === campaignTriggerTypes.TRIGGER_EVENT"
              />
            </el-select>
          </el-col>

          <el-col :span="compact ? 24 : 16">
            <form-item
              label="Value"
              v-if="value.promotion_amount_type === 'fixed'"
              :style="{ marginTop: compact ? '0' : '-30px' }"
            >
              <el-input
                v-model="value.promotion_amount"
                type="number"
                :placeholder="
                  $t('Campaign_PromotionSetup_PromotionAmount_Placeholder')
                "
              />
            </form-item>

            <form-item
              label="Attribute"
              v-if="value.promotion_amount_type === 'attribute'"
              :style="{ marginTop: compact ? '0' : '-30px' }"
            >
              <el-select
                v-model="value.promotion_amount_attribute_title"
                @visible-change="handleVisibleAttributesChange"
                :remote-method="fetchAttributesRemote"
                :loading="attributesLoading"
                :placeholder="
                  $t(
                    'Campaign_PromotionSetup_PromotionAmount_PlaceholderAttribute'
                  )
                "
                filterable
                remote
              >
                <el-option-group
                  :label="$t('Imports_Text_CustomAttributes')"
                  v-if="customAttributes.length"
                >
                  <el-option
                    v-for="(item, index) in customAttributes"
                    :key="index"
                    :label="item.display_name"
                    :value="item.name"
                  />
                </el-option-group>

                <el-option-group
                  :label="$t('Imports_Text_ComputedAttributes')"
                  v-if="computedAttributes.length"
                >
                  <el-option
                    v-for="(item, index) in computedAttributes"
                    :key="index"
                    :label="item.display_name"
                    :value="item.name"
                  />
                </el-option-group>
              </el-select>
            </form-item>

            <form-item
              label="Metric"
              v-if="value.promotion_amount_type === 'metric'"
              :style="{ marginTop: compact ? '0' : '-30px' }"
            >
              <el-select
                v-model="value.promotion_amount_metric_title"
                @visible-change="handleVisibleMetricsChange"
                :remote-method="fetchMetricsRemote"
                :loading="metricsLoading"
                placeholder="Select metric"
                filterable
                remote
              >
                <el-option
                  v-for="(item, index) in metrics"
                  :key="index"
                  :label="item.name"
                  :value="item.title"
                />
              </el-select>
            </form-item>

            <form-item
              label="Transactions window"
              v-if="value.promotion_amount_type === 'metric'"
              style="margin-top: 16px"
            >
              <el-select v-model="value.promotion_amount_window">
                <el-option
                  value="static"
                  label="Within static date range"
                ></el-option>
                <el-option
                  v-if="
                    value.trigger === campaignTriggerTypes.TRIGGER_ACCUMULATION
                  "
                  value="qualification"
                  label="Between first and last transaction in the rule"
                ></el-option>
              </el-select>

              <el-date-picker
                v-model="value.promotion_amount_daterange"
                v-if="value.promotion_amount_window === 'static'"
                type="daterange"
                range-separator="To"
                start-placeholder="Start date"
                end-placeholder="End date"
                style="width: 100%; margin-top: 16px"
              />
            </form-item>

            <form-item
              label="Property"
              v-if="value.promotion_amount_type === 'event'"
              :style="{ marginTop: compact ? '0' : '-30px' }"
            >
              <el-input
                v-model="value.promotion_amount_property_title"
                placeholder="Enter event property"
              />
            </form-item>

            <div
              v-if="
                value.promotion_amount_type &&
                  value.promotion_amount_type !== 'fixed' &&
                  $hasFeature('accumulation')
              "
            >
              <el-row style="margin-top: 16px">
                <el-col :span="span">
                  <form-item label="Apply percents">
                    <el-input
                      v-model="value.promotion_amount_percents"
                      placeholder="100%"
                    />
                  </form-item>
                </el-col>
                <el-col :span="span">
                  <form-item label="Round to decimals">
                    <el-input
                      v-model="value.promotion_amount_round"
                      placeholder="0"
                    />
                  </form-item>
                </el-col>
              </el-row>

              <el-row style="margin-top: 16px">
                <el-col :span="span">
                  <form-item
                    label="Min amount"
                    hint="Bonuses with amount lower than defined will not be issued"
                  >
                    <el-input
                      v-model="value.promotion_amount_min"
                      placeholder="-"
                    />
                  </form-item>
                </el-col>
                <el-col :span="span">
                  <form-item
                    label="Cap amount"
                    hint="Bonuses will be capped by defined amount if they exceed it"
                  >
                    <el-input
                      v-model="value.promotion_amount_cap"
                      placeholder="-"
                    />
                  </form-item>
                </el-col>
              </el-row>
            </div>
          </el-col>
        </el-row>

        <el-input
          v-else-if="param.name === 'duration'"
          v-model="value.promotion_duration"
          type="number"
          :placeholder="
            $t('Campaign_PromotionSetup_PromotionDuration_Placeholder')
          "
        >
          <template slot="append">days</template>
        </el-input>

        <el-input
          v-else-if="param.type === 'string'"
          v-model="value.promotion_params[param.name]"
          type="string"
        ></el-input>

        <el-input
          v-else-if="param.type === 'number'"
          v-model="value.promotion_params[param.name]"
          type="number"
        ></el-input>

        <el-input
          v-else-if="param.type === 'integer'"
          v-model="value.promotion_params[param.name]"
          type="number"
        ></el-input>

        <toggle
          v-else-if="param.type === 'boolean'"
          v-model="value.promotion_params[param.name]"
        ></toggle>
      </form-item>
    </el-col>
  </el-row>
</template>

<script>
import { projectEndpoint } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import FormItem from '@/common/components/form/FormItem'
import Toggle from '@/common/components/form/Toggle'
import { campaignTriggerTypes } from '../../enum/CampaignTriggerTypesEnum'

export default {
  name: 'PromotionSetup',

  components: {
    Toggle,
    FormItem
  },

  props: {
    value: {
      required: true
    },
    compact: {
      default: false
    }
  },

  data() {
    return {
      searchedPromotions: [],
      selectedPromotion: null,
      loading: false,
      searching: false,
      attributes: [],
      attributesLoading: false,
      metrics: [],
      metricsLoading: false,
      campaignTriggerTypes
    }
  },

  mounted() {
    if (this.value.promotion_id) {
      this.loading = true
      this.fetchSelectedPromotion()
    } else {
      this.loading = false
    }
  },

  computed: {
    span() {
      return this.compact ? 24 : 12
    },

    descriptor() {
      return {
        layout: 'div'
      }
    },

    promotionOptionGroups() {
      let groups = []
      let items = this.searchedPromotions

      if (this.selectedPromotion) {
        groups.push({
          name: this.$t(
            'Campaign_PromotionSetup_SelectPromotion_Group_Selected'
          ),
          items: [this.selectedPromotion],
          disabled: true
        })

        items = items.filter(item => {
          return item.id !== this.selectedPromotion.id
        })
      }

      groups.push({
        name: this.searching
          ? this.$t('Campaign_PromotionSetup_SelectPromotion_Group_Filtered')
          : this.$t('Campaign_PromotionSetup_SelectPromotion_Group_Available'),
        items: items,
        disabled: false
      })

      return groups
    },

    selectedPromotionActions() {
      let options = []

      if (this.selectedPromotion) {
        if (this.selectedPromotion.can_add) {
          options.push({
            value: 'add',
            label: this.$t('Campaign_PromotionSetup_SelectAction_Add')
          })
        }

        if (this.selectedPromotion.can_remove) {
          options.push({
            value: 'remove',
            label: this.$t('Campaign_PromotionSetup_SelectAction_Remove')
          })
        }

        if (this.selectedPromotion.can_credit) {
          options.push({
            value: 'credit',
            label: this.$t('Campaign_PromotionSetup_SelectAction_Credit')
          })
        }
      }

      return options
    },

    selectedPromotionActionParams() {
      if (
        this.selectedPromotion &&
        this.selectedPromotion.params &&
        this.value.promotion_action
      ) {
        let params = []
        Object.keys(this.selectedPromotion.params).forEach(name => {
          if (
            this.selectedPromotion.params[name].action ===
            this.value.promotion_action
          ) {
            params.push(
              Object.assign(this.selectedPromotion.params[name], { name })
            )
          }
        })
        return params
      } else {
        return []
      }
    },

    computedAttributes() {
      return this.attributes.filter(attr => attr.aggregate)
    },

    customAttributes() {
      return this.attributes.filter(attr => !attr.aggregate)
    }
  },

  methods: {
    selectPromotion(id) {
      this.selectedPromotion = this.searchedPromotions.find(promotion => {
        return promotion.id === id
      })

      this.value.promotion_action = this.getDefaultPromotionAction(
        this.selectedPromotion
      )
      this.value.promotion_params = {}
      this.value.promotion_amount = null
      this.value.promotion_amount_type = null
      this.value.promotion_amount_attribute_title = null
      this.value.promotion_amount_metric_title = null
      this.value.promotion_amount_property_title = null
      this.value.promotion_amount_window = null
      this.value.promotion_amount_daterange = null
      this.value.promotion_amount_percents = null
      this.value.promotion_amount_round = null
      this.value.promotion_amount_min = null
      this.value.promotion_amount_cap = null
      this.value.promotion_duration = null
    },

    getDefaultPromotionAction(promotion) {
      if (!promotion) {
        return null
      } else if (promotion.can_add) {
        return 'add'
      } else if (promotion.can_credit) {
        return 'credit'
      } else if (promotion.can_remove) {
        return 'remove'
      } else {
        return null
      }
    },

    handleVisibleChange(open) {
      if (open) {
        this.searchPromotions()
      } else {
        this.searching = false
      }
    },

    fetchSelectedPromotion() {
      this.loading = true

      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'promotions/' + this.value.promotion_id,
          params: {
            expand: 'integration_name'
          }
        })
      )
        .then(({ data }) => {
          this.selectedPromotion = data
        })
        .catch(error => {
          this.selectedPromotion = {
            id: this.value.promotion_id,
            name: this.$t('Campaign_PromotionSetup_SelectPromotion_Unknown'),
            description:
              error.response.status === 404
                ? this.$t('Campaign_PromotionSetup_SelectPromotion_Deleted')
                : error.message
          }
        })
        .finally(() => {
          this.loading = false
        })
    },

    searchPromotions(query) {
      this.searching = !!query

      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'promotions',
          params: {
            sort: 'name',
            expand: 'integration_name',
            search: query,
            'per-page': 100
          }
        })
      )
        .then(({ data }) => {
          this.searchedPromotions = data
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    handleAmountTypeChange(type) {
      if (type === 'fixed') {
        this.value.promotion_amount_percents = null
        this.value.promotion_amount_round = null
        this.value.promotion_amount_min = null
        this.value.promotion_amount_cap = null
      } else {
        this.value.promotion_amount = null
      }

      if (type !== 'attribute') {
        this.value.promotion_amount_attribute_title = null
      }

      if (type !== 'metric') {
        this.value.promotion_amount_metric_title = null
        this.value.promotion_amount_window = null
        this.value.promotion_amount_daterange = null
      }

      if (type !== 'event') {
        this.value.promotion_amount_property_title = null
      }
    },

    handleVisibleAttributesChange(open) {
      if (open) {
        this.fetchAttributesRemote()
      }
    },

    handleVisibleMetricsChange(open) {
      if (open) {
        this.fetchMetricsRemote()
      }
    },

    fetchAttributesRemote(query) {
      let params = { sort: 'title', attribute_or_aggregate: 1, type: '1,5,6' }
      if (query !== null && query !== '') {
        params.title = query
      }

      this.attributesLoading = true

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `tags`,
          params: params
        })
      )
        .then(({ data }) => {
          this.attributes = data
          this.attributesLoading = false
        })
        .catch(error => {
          this.attributesLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    fetchMetricsRemote(query) {
      let params = { sort: 'title', type: '1,5,6' }
      if (query !== null && query !== '') {
        params.title = query
      }

      this.metricsLoading = true

      RestApi(
        projectEndpoint({
          method: 'get',
          url: `metrics`,
          params: params
        })
      )
        .then(({ data }) => {
          this.metrics = data
          this.metricsLoading = false
        })
        .catch(error => {
          this.metricsLoading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  }
}
</script>

<style scoped>
.compact {
  width: 100%;
}

>>> .el-form-item {
  margin-bottom: 20px;
}

.compact >>> .el-form-item {
  margin-bottom: 10px;
}

>>> .el-form-item:last-child {
  margin-bottom: 0;
}

.compact .params {
  margin-top: 10px;
  padding-left: 40px !important;
}

.compact >>> .promotion .el-form-item__label {
  display: none;
}

.compact >>> .params .el-form-item__label {
  margin-right: 8px;
  line-height: 40px;
}

.compact >>> .params .el-form-item__content {
  width: 200px;
}

.compact >>> .params .el-form-item {
  display: flex;
  justify-content: right;
}

.compact >>> .params .amount .el-form-item {
  display: block;
}

.compact >>> .params .hint {
  display: none;
}

.dropdown-item {
  height: auto;
  padding: 8px 20px;
  line-height: 20px;
  white-space: normal;
}

.dropdown-item .tags span {
  margin-top: 4px;
}

.dropdown-item .description {
  color: #7e8596;
  font-size: 11px;
}

.compact .promotion-amount-type {
  margin-bottom: 10px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/setup/PromotionSetup.vue